import React from 'react';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';

const CATEGORY_CONFIG = {
  preferences: { color: 'bg-blue-100 text-blue-700', label: 'Preferences' },
  note: { color: 'bg-purple-100 text-purple-700', label: 'Note' },
  location: { color: 'bg-green-100 text-green-700', label: 'Location' },
  account: { color: 'bg-amber-100 text-amber-700', label: 'Account' },
  status: { color: 'bg-red-100 text-red-700', label: 'Status' },
};

const actionLabel = (action) => (action === 'create' ? 'Created' : action === 'delete' ? 'Deleted' : 'Updated');

const OrderInvoiceRow = ({ primary, secondary, badge, badgeClass, amount }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
    <div>
      <p className="font-medium">{primary}</p>
      <p className="text-sm text-gray-500">{secondary}</p>
    </div>
    <div className="text-right">
      <Badge className={badgeClass}>{badge}</Badge>
      <p className="font-medium mt-1">${(amount || 0).toFixed(2)}</p>
    </div>
  </div>
);

export const HistoryTab = ({ clientDetail, auditLog, auditPage, loadingAudit, onPageChange }) => (
  <div className="mt-4">
    <div className="space-y-6">
      {/* Recent Orders */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Recent Orders</p>
        {clientDetail.recent_orders?.length > 0 ? (
          <div className="space-y-2">
            {clientDetail.recent_orders.map((order) => (
              <OrderInvoiceRow
                key={order.id}
                primary={order.order_number}
                secondary={new Date(order.created_at).toLocaleDateString()}
                badge={order.status}
                badgeClass={
                  order.status === 'delivered' ? 'bg-green-100 text-green-800'
                    : order.status === 'cancelled' ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                }
                amount={order.total_amount}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No orders</p>
        )}
      </div>

      {/* Recent Invoices */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Recent Invoices</p>
        {clientDetail.invoices?.length > 0 ? (
          <div className="space-y-2">
            {clientDetail.invoices.map((inv) => (
              <OrderInvoiceRow
                key={inv.id}
                primary={inv.invoice_number}
                secondary={`Due: ${new Date(inv.due_date).toLocaleDateString()}`}
                badge={inv.status}
                badgeClass={inv.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                amount={inv.amount}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No invoices</p>
        )}
      </div>

      {/* Change History / Audit Log */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-amber-500" />
          <p className="text-sm font-medium text-gray-700">Change History</p>
          {auditLog.total > 0 && (
            <span className="text-xs text-gray-400">({auditLog.total} entries)</span>
          )}
        </div>
        {loadingAudit ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
          </div>
        ) : auditLog.entries?.length > 0 ? (
          <>
            <div className="space-y-3" data-testid="audit-log-list">
              {auditLog.entries.map((entry, idx) => {
                const config = CATEGORY_CONFIG[entry.category] || { color: 'bg-gray-100 text-gray-700', label: entry.category };
                return (
                  <div key={entry.id || `${entry.created_at}-${idx}`} className="p-3 border border-gray-100 rounded-lg" data-testid={`audit-entry-${idx}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${config.color} text-xs`}>{config.label}</Badge>
                          <span className="text-xs font-medium text-gray-500">{actionLabel(entry.action)}</span>
                        </div>
                        <p className="text-sm mt-1 text-gray-800">{entry.summary}</p>
                        {entry.changes?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {entry.changes.map((change) => (
                              <div key={`${change.field}-${change.old}-${change.new}`} className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                                <span className="font-medium text-gray-600">{change.field}:</span>
                                <span className="line-through text-red-400">{change.old != null ? String(change.old) : '(empty)'}</span>
                                <span className="text-gray-400">&rarr;</span>
                                <span className="text-green-600 font-medium">{change.new != null ? String(change.new) : '(empty)'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-gray-400">
                          {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 truncate max-w-[140px]">{entry.changed_by}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {auditLog.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t" data-testid="audit-pagination">
                <p className="text-xs text-gray-500">Page {auditLog.page} of {auditLog.total_pages}</p>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditLog.page <= 1}
                    onClick={() => onPageChange(auditPage - 1)}
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditLog.page >= auditLog.total_pages}
                    onClick={() => onPageChange(auditPage + 1)}
                    data-testid="audit-next-page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6 text-gray-400">
            <History className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No change history yet</p>
          </div>
        )}
      </div>
    </div>
  </div>
);
